import axios from "axios";
import { message } from "antd";
import qs from 'qs';
import { clearAuthCookies, getAuthCookieValue } from "./authCookie";
import { getLoginPathForTenantContext } from "./tenantFromHost";

// Flag to prevent multiple 401 error messages
let isHandling401Error = false;
let authRedirectTimeoutId = null;

export const cancelPendingAuthRedirect = () => {
  if (authRedirectTimeoutId) {
    clearTimeout(authRedirectTimeoutId);
    authRedirectTimeoutId = null;
  }
  isHandling401Error = false;
};

const buildLoginRedirectUrl = (slug = "") => {
  const loginPath = getLoginPathForTenantContext({ slug });
  const { pathname = "/", search = "", hash = "" } = window.location;
  const nextPath = `${pathname}${search}${hash}`;
  const params = new URLSearchParams();

  if (pathname && !pathname.includes("/login")) {
    params.set("next", nextPath);
  }

  const query = params.toString();
  return query ? `${loginPath}?${query}` : loginPath;
};

const httpBase = (api = true) => {
  const ngrokSkipValue = process.env.VITE_NGROK_SKIP_BROWSER_WARNING || "true";

  const instance = axios.create({
    baseURL: `${process.env.VITE_API_BASE_URL}`,
    paramsSerializer: {
      serialize: (params) => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      }
    }
  });

  instance.interceptors.request.use(
    (config) => {
      if (config.headers) {
        config.headers["ngrok-skip-browser-warning"] = ngrokSkipValue;
      } else {
        config.headers = {
          "ngrok-skip-browser-warning": ngrokSkipValue,
        };
      }

      const authToken = getAuthCookieValue("authToken");

      if (authToken) {
        if (config.headers)
          config.headers.Authorization = `Bearer ${authToken}`;
      }
      return config;
    },
    (error) => {
      // Handle request errors here
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalConfig = error.config;
      console.log("Error in second instance.interceptors.response -> ", error);

      if (
        error?.response?.data?.detail === "Could not validate credentials" &&
        !originalConfig._retry
      ) {
        originalConfig._retry = true;

        // Prevent multiple 401 error handling
        if (!isHandling401Error) {
          isHandling401Error = true;
          const failedAuthToken = getAuthCookieValue("authToken");

          // Clear user session
          clearAuthCookies();
          localStorage.removeItem("tenantSetupCompleted");
          localStorage.removeItem("tutorialComplete");

          // Display message to user and delay the redirect
          message.error("Your session has expired. Please log in again.", 2.5);

          // Delay the redirect to allow time for the message to be seen
          authRedirectTimeoutId = setTimeout(() => {
            authRedirectTimeoutId = null;

            const currentAuthToken = getAuthCookieValue("authToken");
            if (currentAuthToken && currentAuthToken !== failedAuthToken) {
              isHandling401Error = false;
              return;
            }

            const slug = getAuthCookieValue("slug");
            window.location.href = buildLoginRedirectUrl(slug);
            // Reset the flag after redirection (although page will reload)
            isHandling401Error = false;
          }, 2000); // 2 seconds delay
        }
      } else if (error?.response?.status === 500) {
        // Handle server errors (500) if needed
        message.error("Server error. Please try again later.", 3);
      } else if (error?.response?.status === 403) {
        // Handle forbidden errors (403) if needed
        message.error("You don't have permission to access this resource.", 3);
      } else if (error?.response?.data?.message) {
        // Handle other error messages from the API
        message.error(error.response.data.message, 3);
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

export const handleApiExceptions = (errors) => {
  console.log("caught api exception", errors);

  // Don't show duplicate messages for errors already handled in interceptor
  if (
    errors?.response?.status === 401 ||
    errors?.response?.status === 500 ||
    errors?.response?.status === 403
  ) {
    return;
  }

  // Show error message for other exceptions
  if (errors?.response?.data?.message) {
    message.error(errors.response.data.message, 3);
  } else if (errors?.message) {
    message.error(errors.message, 3);
  }
};

export default httpBase;
